<template>
  <tbody>
    <tr
      v-for="(item, trIndex) in bodyList"
      :key="`${trIndex}-tr`"
    >
      <template
        v-for="(value, tdIndex) in Object.values(item)"
        :key="`${tdIndex}-td`"
      >
        <td
          v-if="!excludeList.includes(headerList[tdIndex])"
          colspan="20"
          @click="getField(trIndex, tdIndex, value)"
        >
          <!-- Material select -->
          <div
            v-if="headerList[tdIndex] === 'element_material'"
            :class="{
              'typo-small-title': true,
              'issue-inline-field--error': !validateMaterial(bodyList[trIndex][headerList[tdIndex]])
            }"
          >
            <Select
              :items="elementMaterialOptions"
              :modelValue="bodyList[trIndex][headerList[tdIndex]]"
              :placeholder="$t('issues.unspecified')"
              @update:model-value="updateSelect"
            />
          </div>
          <!-- Class select -->
          <div
            v-else-if="headerList[tdIndex] === 'element_class'"
            :class="{
              'typo-small-title': true,
              'issue-inline-field--error': !validateClass(bodyList[trIndex][headerList[tdIndex]])
            }"
          >
            <DescriptionSelect
              v-if="elementClassOptions.length > 0"
              v-model="bodyList[trIndex][headerList[tdIndex]]"
              :items="elementClassOptions"
              :label="$t('issues.class')"
              name="element_class"
              :placeholder="$t('issues.unspecified')"
              :removable="false"
              :simple="true"
            />
          </div>
          <!-- Det mech section -->
          <div
            v-else-if="['deterioration_mechanism_ratings_attributes'].includes(headerList[tdIndex])"
            class="issue-ratings__section"
          >
            <div
              class="issue-inline-field issue-inline-field--heading"
            >
              <div class="issue-inline-field__field">
                <div
                  v-if="Object.keys(deteriorationMechanisms(trIndex)).length > 0"
                  class="select select--input issue-ratings__dropdown"
                >
                  <div class="dropdown">
                    <Popper
                      v-model:open="bodyList[trIndex]['det_mech_panel']"
                      placement="bottom-end"
                    >
                      <template #trigger>
                        <div class="dropdown__trigger">
                          <div class="select__trigger issue-ratings__drop-trigger">
                            <button
                              :aria-label="$t('issues.add_deterioration_mechanism_rating')"
                              class="select__label select__label--flex"
                              type="button"
                            >
                              <Plus16Svg />
                            </button>

                            <span class="select__arrow">
                              <UseSvg
                                class="icon icon--small"
                                name="arrow_down_16"
                              />
                            </span>
                          </div>
                        </div>
                      </template>

                      <div
                        v-if="!bodyList[trIndex]['new_rating']"
                        class="menu menu--dropdown"
                      >
                        <ul class="menu__items">
                          <li
                            v-for="(detMech, name) in deteriorationMechanisms(trIndex)"
                            :key="`${name}-det-mech`"
                            class="menu-item menu-item--dropdown"
                          >
                            <button
                              class="menu-item__link"
                              type="button"
                              @click="createNewRating(trIndex, name, detMech)"
                            >
                              <span class="menu-item__label">{{
                                $t(`deterioration_mechanisms.${name}.name`)
                              }}</span>
                            </button>
                          </li>
                        </ul>
                      </div>

                      <DescriptionSelectDropdown
                        v-if="bodyList[trIndex]['new_rating']"
                        :addText="$t('issues.add_rating')"
                        iconName="rating_circle"
                        :items="ratingItems(trIndex, bodyList[trIndex]['new_rating'])"
                        :label="$t(`deterioration_mechanisms.${bodyList[trIndex]['new_rating'].name}.name`)"
                        :name="`new_rating_${trIndex}`"
                        :ratingScale="true"
                        :removable="false"
                        @cancel="cancelNewRating(trIndex)"
                        @update:model-value="addRating(trIndex, $event)"
                      />
                    </Popper>
                  </div>
                </div>
              </div>
            </div>

            <div class="issue__ratings-container">
              <div
                v-for="rating in bodyList[trIndex]['deterioration_mechanism_ratings_attributes']"
                :key="`${rating.id}-rating`"
                :class="{
                  'issue-inline-field': true,
                  'issue-inline-field--error': validateRating(rating)
                }"
              >
                <div class="issue-inline-field__title">
                  <template v-if="validateRating(rating)">
                    {{ $t('issues.import_error') }}
                  </template>
                  <template v-else>
                    {{ $t(`deterioration_mechanisms.${rating.name}.name`) }}
                  </template>
                </div>
                <div class="issue-inline-field__field">
                  <DescriptionSelect
                    :hideRate="validateRating(rating)"
                    iconName="rating_circle"
                    :items="ratingItems(trIndex, rating)"
                    :label="$t(`deterioration_mechanisms.${rating.name}.name`)"
                    :modelValue="rating.value"
                    :name="rating?.name || ''"
                    :ratingScale="true"
                    :selectIcon="true"
                    selectClasses="issue-ratings__dropdown"
                    @remove="removeRating(trIndex, rating)"
                    @update:model-value="updateRating(rating, $event)"
                  />
                </div>
              </div>
              <div
                v-if="bodyList[trIndex]['deterioration_mechanism_ratings_attributes'].length === 0"
                class="issue-inline-field issue-inline-field--empty issue-inline-field--transparent"
              >
                <div class="issue-inline-field__icon">
                  <ExclamationTriangleSvg />
                </div>
                <div class="issue-inline-field__title">
                  {{ $t('issues.none_specified') }}
                </div>
              </div>
            </div>
          </div>
          <!-- Image upload section -->
          <div
            v-else-if="['images'].includes(headerList[tdIndex])"
            class="table__upload-group"
          >
            <label
              class="table__upload-button button"
              :for="`image-${trIndex}`"
            >
              <FileUploader
                :id="`image-${trIndex}`"
                :acceptedFileTypes="acceptedFileTypes"
                fileType="image"
                :label="$t('issues.add_images')"
                :modifiers="['always-show', 'position-relative', 'right-unset', 'width-auto']"
                :multiple="true"
                @update-reader="updateReader($event, trIndex)"
              />
              <p class="table__upload-text">{{ $t('issues.add_images') }}</p>
            </label>
            <div class="table__image-group">
              <div
                v-for="(image, imageIndex) in bodyList[trIndex][headerList[tdIndex]]"
                :key="`${image.name}-${trIndex}-${tdIndex}`"
                class="table__image-list"
              >
                <p class="table__image-name">
                  {{ image.name }}
                </p>
                <Dropdown
                  :items="actionMenuList"
                  @click-item="setDialogType($event, trIndex, imageIndex)"
                >
                  <UseSvg
                    class="icon"
                    name="vertical_ellipsis"
                  />
                </Dropdown>
              </div>
            </div>
          </div>
          <!-- Text section -->
          <p v-else-if="value && (['index', 'image'].includes(headerList[tdIndex]) || !bodyList[trIndex].edit)">
            {{ value }}
          </p>
          <!-- Input section -->
          <div
            v-else
            :class="{ input: true, 'input--error': !value }"
          >
            <input
              :id="`input-${trIndex}-${tdIndex}`"
              :aria-label="`input-${trIndex}-${tdIndex}`"
              type="text"
              :value="value"
              @focusout="updateValue"
              @keyup="updateField"
            >
          </div>
        </td>
      </template>
    </tr>
  </tbody>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';
import I18n from 'i18n-js';
import { v4 as uuid } from 'uuid';

// Utils
import { acceptedFileTypes } from '@/config/accepted_file_types';
import DETERIORATION_MECHANISMS, {
  DEFAULT_RATINGS,
} from '@/functions/simm/deterioriation_mechanisms';

// Components
import ExclamationTriangleSvg from '@/images/exclamation-triangle.svg';
import Plus16Svg from '@/images/plus-16.svg';
import UseSvg from '@/components/generic/use_svg.vue';
import DescriptionSelect from '@/components/form/description_select.vue';
import DescriptionSelectDropdown from '@/components/form/description_select_dropdown.vue';
import Select from '@/components/form/select.vue';
import Popper from '@/components/generic/popper.vue';
import FileUploader from '@/components/generic/file_uploader.vue';
import Dropdown from '@/components/generic/dropdown.vue';

const createNewData = () => ({
  tr: null,
  td: null,
  header: '',
  label: '',
  newValue: ''
});

const props = defineProps({
  /** Header title list */
  headerList: { type: Array, required: true, default: () => [] },
  /** Data for the table body */
  bodyList: { type: Array, required: true, default: () => [] },
  /** List of columns to be excluded from the body view */
  excludeList: { type: Array, required: false, default: () => [] },
  /**
   * Current loading state of the incoming data,
   * Expects `plant`, `ship`, `shaft` or `equipment`.
   */
  identifier: {
    type: String,
    required: true,
    default: 'plant',
    validator(value) {
      return ['plant', 'ship', 'shaft', 'equipment'].includes(value);
    }
  }
});

const editData = ref(createNewData());
const editedList = [];
const editIndex = ref('');
const newRating = ref(null);

/**
 * Provide element material options for selection.
 */
const elementMaterialOptions = computed(() => {
  return [
    'steel',
    'concrete',
    'fibreglass',
    'slope',
    'asbestos',
    'timber'
  ].filter((element) => {
    return element == 'timber' || [props?.identifier].includes('plant');
  }).map((value) => ({
    label: I18n.t(`element_materials.${value}`),
    value,
  }));
});

const elementClassOptions = [
  'primary',
  'secondary',
  'tertiary',
  'non_structural',
].map((value) => ({
  label: I18n.t(`element_classes.${value}.name`),
  value,
  description: [I18n.t(`element_classes.${value}.description`)],
  disabled: false,
}));

const actionMenuList = computed(() => {
  return [{
    label: I18n.t('general.delete'),
    key: 'delete',
    modifiers: ['destructive']
  }];
});

/**
 * Toggles dialog when the delete button is clicked
 */
function setDialogType({ key }, trIndex, imageIndex) {
  if (key === 'delete') {
    props.bodyList[trIndex].images.splice(imageIndex, 1);
  }
}

function cancelNewRating(trIndex) {
  props.bodyList[trIndex]['det_mech_panel'] = false;
  props.bodyList[trIndex]['new_rating'] = null;
}

async function addRating(trIndex, rating) {
  props.bodyList[trIndex]['deterioration_mechanism_ratings_attributes'].push({
    id: uuid(),
    name: props.bodyList[trIndex]['new_rating'].name,
    value: rating,
  });

  props.bodyList[trIndex]['det_mech_panel'] = false;
  props.bodyList[trIndex]['new_rating'] = null;
}

function deteriorationMechanisms(trIndex) {
  const material = props.bodyList[trIndex]['element_material'];

  if (!material) return {};

  let dms = { ...DETERIORATION_MECHANISMS[material] };
  const detMechRatings = props.bodyList[trIndex]['deterioration_mechanism_ratings_attributes'];

  detMechRatings.forEach((r) => delete dms[r.name]);

  return dms;
}

function ratingItems(trIndex, detMech) {
  const material = props.bodyList[trIndex]['element_material'];
  const isValidMaterial = validateMaterial(material);
  const detMechName = detMech.name?.toLowerCase();
  if (detMechName === '' || !isValidMaterial) return [];
  const enabledRatings = DETERIORATION_MECHANISMS[material][detMechName]?.ratings;

  return DEFAULT_RATINGS.map((rating) => ({
    value: rating,
    label: rating.toString(),
    description: validateTranslation(I18n.t(`dmr_descriptions.${material}.${detMechName}.${rating}`)),
    disabled: !enabledRatings?.includes(rating),
    iconClass: `issue-ratings__indicator issue-ratings__indicator--condition-${rating}`,
  }));
}

async function removeRating(trIndex, rating) {
  let detMechList = props.bodyList[trIndex]['deterioration_mechanism_ratings_attributes'];
  props.bodyList[trIndex]['deterioration_mechanism_ratings_attributes'] = detMechList.filter(
    (x) => x.id !== rating.id
  );
}

async function updateRating(rating, value) {
  rating.value = value;
}

function createNewRating(trIndex, name, detMech) {
  props.bodyList[trIndex]['new_rating'] = { name, ...detMech };
  newRating.value = { name, ...detMech };
  window.dispatchEvent(new Event('resize')); // Force popper to reposition
}

function updateSelect(value) {
  props.bodyList[editData.value.tr][Object.keys(props.bodyList[editData.value.tr])[editData.value.td]] = value;
  props.bodyList[editData.value.tr]['deterioration_mechanism_ratings_attributes'] = [];
}

function updateField(event) {
  editData.value.newValue = event.target.value;
}

function updateValue() {
  props.bodyList[editData.value.tr][Object.keys(props.bodyList[editData.value.tr])[editData.value.td]] = editData.value.newValue;
}

async function getField(trIndex, tdIndex, value) {
  props.bodyList.forEach((item, index) => { if (index !== trIndex) item.edit = false; });
  await nextTick(() => {
    editData.value.tr = trIndex;
    editData.value.td = tdIndex;
    editData.value.newValue = value;
    if (!props.bodyList[trIndex]['edit']) {
      editedList.push(trIndex);
      props.bodyList[trIndex]['edit'] = true;
      editIndex.value = trIndex;
    }
  });
}

function validateRating(rating) {
  return (typeof rating.value === 'undefined') ||
    !validateTranslation(I18n.t(`deterioration_mechanisms.${rating.name}.name`)) ||
    validateDetMechRating(rating) ||
    validateRatingValue(rating);
}

// Only accept ratings of 0, 4, 5 for suspicious_design_or_manufacturing
function validateDetMechRating(rating) {
  const isInvalid = rating.name === 'suspicious_design_or_manufacturing' && [1, 2, 3].includes(rating.value);
  isInvalid && (rating.value = 'undefined');
  return isInvalid;
}

// Only accept ratings from 0 - 5 for every other detMech
function validateRatingValue(rating) {
  return ![0, 1, 2, 3, 4, 5].includes(rating.value);
}

function validateTranslation(text) {
  const isInvalidTranslation = text.includes('missing') && text.includes('translation');
  if (isInvalidTranslation) return;
  return text;
}

function validateMaterial(material) {
  return elementMaterialOptions.value.find(item => item.value === material);
}

function validateClass(className) {
  return elementClassOptions.find(item => item.value === className);
}

function updateReader(file, index) {
  if (!file?.url) return;
  props.bodyList[index].images.push({ ...file, name: file.image_blob.filename });
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

td {
  border: bu(.2) solid map-get($background-color, dark);
  padding: bu(1);
}

td:nth-child(2) {
  p {
    max-width: bu(35);
  }
}

td:nth-child(3), td:nth-child(4) {
  p {
    max-width: bu(80);
  }
}

td:nth-child(5), td:nth-child(6) {
  p {
    max-width: bu(25);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

tr:nth-child(odd) {
  background-color: map-get($background-color, default);
}

tr:nth-child(even) {
  background-color: map-get($background-color, light);
}

tr:hover {
  background-color: map-get($background-color, darker);
}

.input {
  background-color: map-get($background-color, dark);
  color: map-get($foreground-color, light);
  border: 0 solid transparent;
  border-radius: bu(.5);
  padding: bu(.2);
  width: 100%;
}

.issue {
  &-ratings {
    &__section {
      display: flex;
      flex-direction: row-reverse;
      align-items: flex-start;
      justify-content: space-between;
      padding-top: 0;
    }
  }
  &__ratings {
    &-container {
      width: 100%;
    }
  }
}

.table {
  &__image-group {
    max-height: bu(45);
    overflow-y: scroll;
  }
  &__image-list {
    display: flex;
    align-items: center;
    padding: bu(1) bu(1) bu(1) bu(2.5);
    background-color: map-get($background-color, light);
    color: $input-bg-tint;
    margin-top: rem(3);
    min-height: rem(48);
    justify-content: space-between;
    margin-right: bu(.8);
  }
  &__image-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: bu(30);
  }
  &__upload-group {
    display: flex;
    flex-direction: row-reverse;
    justify-content: center;
  }
  &__upload-button {
    margin: bu(.8) bu(1);
  }
  &__upload-text {
    margin-left: bu(1);
    display: none;
    @include breakpoint(medium) {
      display: block;
    }
  }
}
</style>
